import { reactive, readonly } from 'vue';

/**
 * Cross-navigation context bus.
 *
 * A module-singleton reactive store the Inertia app writes into and the
 * persistent overlay layer reads. Because it is a module singleton it outlives
 * Inertia page navigations (the page tree re-mounts, this does not), so overlay
 * panels — a datagrid-settings debug panel, say — can reflect whatever the
 * current page publishes without being torn down between routes.
 */
const store = reactive<Record<string, unknown>>({});

export interface ContextBus {
    /** Publish (replace) the value under a namespace key. */
    publish(key: string, value: unknown): void;
    /** Remove a namespace — call on unmount so stale context does not linger. */
    clear(key: string): void;
    /** Read the current value under a key. */
    get<T = unknown>(key: string): T | undefined;
    /** Read-only reactive view of the whole bus. */
    state: Readonly<Record<string, unknown>>;
}

export function useContextBus(): ContextBus {
    return {
        publish(key, value) {
            store[key] = value;
        },
        clear(key) {
            delete store[key];
        },
        get<T = unknown>(key: string): T | undefined {
            return store[key] as T | undefined;
        },
        state: readonly(store),
    };
}
